/**
 * Roth conversion ladder modeler — Phase 7 Session 1.
 *
 * **The pattern**: convert pre-tax IRA → Roth IRA in deliberate
 * year-by-year tranches BEFORE 59½ to access retirement money penalty-
 * free. Each conversion's principal is withdrawable 5 years later
 * (per IRC § 408A(d)(3)(B)(iii) + Notice 2008-30) — that's the
 * "ladder": stagger conversions 5 years apart so each tranche becomes
 * available exactly when needed.
 *
 * **Math**:
 *
 *   For each (year, conversion_amount) tuple, compute:
 *     - incremental_tax = taxLiability(baseline + conversion) -
 *                         taxLiability(baseline)
 *     - federal_tax_cents, ny_tax_cents, nyc_tax_cents per the
 *       composite breakdown.
 *     - available_at_year = year + 5 (5-year clock from each
 *       conversion).
 *     - age_at_conversion = current_age + (year - current_year).
 *
 *   Compare to baseline scenario (no conversions). The "delta" is the
 *   user's lifetime tax cost of choosing the ladder vs. doing nothing
 *   pre-retirement (in which case RMDs at age 73+ force higher
 *   marginal rates on bigger tranches).
 *
 * **Authority**:
 *   - IRC § 408A(d)(3) — Roth conversion mechanics + 5-year clock.
 *   - Notice 2008-30 — pro-rata rule for partial conversions.
 *
 * **v1 scope**:
 *   - Year-by-year input (user picks per-year amounts in the SPA).
 *   - Fixed `baseline_taxable_income_cents` for tax-cost computation.
 *     Real life: baseline drifts with inflation, RSU vests, etc.
 *     Phase 11 polish may layer in per-year baseline overrides.
 *   - Doesn't model NIIT, IRMAA Medicare premium surcharges, ACA
 *     subsidy phase-outs (Cleave 7 ACA helper handles those
 *     downstream). Phase 11 may bundle them.
 *   - Doesn't model investment growth on unconverted balance — focuses
 *     on the tax cost only.
 *
 * **Web mirror** at `web/src/lib/util/roth-ladder.ts`.
 */

import { taxLiability } from "./tax-liability.js";
import type { FilingStatus } from "../data/tax-brackets-2025.js";

export interface RothConversionInput {
  readonly year: number;
  readonly amount_cents: number;
}

export interface RothLadderInput {
  readonly conversions: readonly RothConversionInput[];
  readonly current_age: number;
  /** Calendar year of `current_age` — defaults to the helper's
   *  computation date year if omitted. */
  readonly current_year: number;
  /** Filing status assumed across the ladder period. */
  readonly filing_status: FilingStatus;
  /** Baseline taxable income that conversions stack ON TOP OF. Same
   *  every year in v1 (held flat). */
  readonly baseline_taxable_income_cents: number;
  /** Whether to compute NY State liability. Default true. */
  readonly include_ny?: boolean;
  /** Whether to compute NYC resident liability. Default true. */
  readonly include_nyc?: boolean;
  /** Tax-year for bracket lookup. Hearth ships 2025 + 2026. Defaults
   *  to current_year clamped to supported range. */
  readonly tax_year_for_brackets?: number;
}

export interface RothLadderRow {
  readonly year: number;
  readonly conversion_cents: number;
  readonly age_at_conversion: number;
  readonly available_at_year: number;
  readonly available_at_age: number;
  readonly federal_tax_cents: number;
  readonly ny_tax_cents: number;
  readonly nyc_tax_cents: number;
  readonly total_tax_cents: number;
  readonly composite_marginal_rate_pct: number;
}

export interface RothLadderResult {
  readonly schedule: readonly RothLadderRow[];
  /** Sum of `total_tax_cents` across the ladder. */
  readonly lifetime_total_tax_cents: number;
  /** Tax cost of the BASELINE (no conversions) over the same years. */
  readonly baseline_lifetime_total_tax_cents: number;
  /** Difference — positive means the ladder cost more in tax than
   *  doing nothing. (Doesn't capture the avoided RMD penalty + reduced
   *  IRMAA exposure later — Phase 11.) */
  readonly delta_cents: number;
  /** Total dollars converted across the ladder. */
  readonly total_converted_cents: number;
}

export function rothLadder(input: RothLadderInput): RothLadderResult {
  if (input.conversions.length === 0) {
    return {
      schedule: [],
      lifetime_total_tax_cents: 0,
      baseline_lifetime_total_tax_cents: 0,
      delta_cents: 0,
      total_converted_cents: 0,
    };
  }
  const include_ny = input.include_ny ?? true;
  const include_nyc = input.include_nyc ?? true;
  const tax_year = clampTaxYear(
    input.tax_year_for_brackets ?? input.current_year,
  );

  const rows: RothLadderRow[] = [];
  let lifetime = 0;
  let baseline_lifetime = 0;
  let total_converted = 0;

  for (const c of input.conversions) {
    const age_at_conversion =
      input.current_age + (c.year - input.current_year);

    // Baseline tax for this year (no conversion).
    const baselineLiability = taxLiability({
      ordinary_income_cents: input.baseline_taxable_income_cents,
      ltcg_cents: 0,
      qdivs_cents: 0,
      filing_status: input.filing_status,
      tax_year,
      include_ny,
      include_nyc,
    });
    baseline_lifetime += baselineLiability.total_cents;

    if (c.amount_cents <= 0) {
      // Empty conversion year — track baseline only, no row emitted.
      continue;
    }

    // With-conversion tax (conversion adds to ordinary income).
    const withConversion = taxLiability({
      ordinary_income_cents:
        input.baseline_taxable_income_cents + c.amount_cents,
      ltcg_cents: 0,
      qdivs_cents: 0,
      filing_status: input.filing_status,
      tax_year,
      include_ny,
      include_nyc,
    });

    const incremental_total = withConversion.total_cents - baselineLiability.total_cents;
    const incremental_federal =
      withConversion.federal_cents - baselineLiability.federal_cents;
    const incremental_ny = withConversion.ny_cents - baselineLiability.ny_cents;
    const incremental_nyc =
      withConversion.nyc_cents - baselineLiability.nyc_cents;

    rows.push({
      year: c.year,
      conversion_cents: c.amount_cents,
      age_at_conversion,
      available_at_year: c.year + 5,
      available_at_age: age_at_conversion + 5,
      federal_tax_cents: incremental_federal,
      ny_tax_cents: incremental_ny,
      nyc_tax_cents: incremental_nyc,
      total_tax_cents: incremental_total,
      composite_marginal_rate_pct: withConversion.composite_marginal_rate_pct,
    });
    lifetime += incremental_total;
    total_converted += c.amount_cents;
  }

  return {
    schedule: rows,
    lifetime_total_tax_cents: lifetime,
    baseline_lifetime_total_tax_cents: baseline_lifetime,
    delta_cents: lifetime - 0, // ladder-cost beyond baseline; same as `lifetime`
    total_converted_cents: total_converted,
  };
}

function clampTaxYear(year: number): number {
  if (year < 2025) return 2025;
  if (year > 2026) return 2026;
  return year;
}
